{/*
  * DspDeletedDeviceIdsAPIPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/gdpr-deleted-device-ids-api
  * Documentation > MAX Demand Partners > DSPs > GDPR Deleted Device IDs API
  */}
import React, { FunctionComponent } from 'react';
import { DataTable, DocsPageModule } from 'UI-Dash-Commons-UI';
import CodeSnippet from '../containers/CodeSnippetContainer';
import styled from 'styled-components';

type DspDeletedDeviceIdsApiPageProps = {
    linkTargetTopPosition?: number;
}

const DspDeletedDeviceIdsApiPage: FunctionComponent<DspDeletedDeviceIdsApiPageProps> = ( { linkTargetTopPosition } ) =>
{
    return ( <>
        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'GDPR Deleted Device IDs API'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_1}/>

        <DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContentText
                text={'As a participant in AppLovin’s auction, and as a party to a DPA signed with AppLovin, you have acknowledged that you will promptly delete any Personal Data where such person has requested deletion. '
                + 'These deletions must be done no less frequently than on a weekly basis. '
                + 'AppLovin recommends that you automate the deletion process by means of this API, preferably on a daily basis.'}/>
        </DocsPageModule.DocsPageContent>

        <DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContentText
                text={"The available API is:"}/>
        </DocsPageModule.DocsPageContent>

        <DataTable
            data={[
                {
                    attribute: '\`list_idfa\`',
                    value: 'List device advertising IDs for which, in a given period of time, the owner of the device requested deletion of personal data.'
                },
            ]}
            columns={
                [
                    {
                        id: 'attribute',
                        Header: 'Endpoint',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.attribute}/>
                    },
                    {
                        id: 'value',
                        Header: 'Purpose',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.value}/>
                    },
                ]
            }
            totalRowCount={1}
        />

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Authentication'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContentText text={'The GDPR API requires your GDPR API key in the '}/>
            <DocsPageModule.DocsPageContentCode children={'api_key'}/>
            <DocsPageModule.DocsPageContentText text={' parameter of the request. You can find your GDPR API key in your AppLovin account ('} />
            <DocsPageModule.DocsPageContentAnchor
                text={'Account > General > Keys'}
                to={'/account#keys'}
                newWindow={true}
            />
            <DocsPageModule.DocsPageContentText text={').'} />
        </DocsPageModule.DocsPageContent>


        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Building the Query'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContentText
                text={'To get a list of all device IDs for which the device owner requested information deletion, for GDPR compliance, make an HTTP '}/>
            <DocsPageModule.DocsPageContentCode children={'GET'}/>
            <DocsPageModule.DocsPageContentText text={' request to the list API:'}/>
            <CodeSnippet
                snippets={[ {
                    title: 'text',
                    language: 'plaintext',
                    code: `https://api.applovin.com/gdpr_dsp/list_idfa`
                } ]}
            />
        </DocsPageModule.DocsPageContent>

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Query Parameters'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <DataTable
            data={[
                {
                    name: '\`api_key\`',
                    type: 'String',
                    description: 'DSP GDPR API Key',
                    required: 'Yes',
                },
                {
                    name: '\`start_date\`',
                    type: 'Date',
                    description: 'Valid Format is \`YYYY-MM-DD\` . Maximum look-back window is seven days.',
                    required: 'No',
                },
                {
                    name: '\`end_date\`',
                    type: 'Date',
                    description: 'Valid Format is \`YYYY-MM-DD\`',
                    required: 'No',
                },
            ]}
            columns={
                [
                    {
                        id: 'name',
                        Header: 'Name',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.name}/>
                    },
                    {
                        id: 'type',
                        Header: 'Type',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                    },
                    {
                        id: 'description',
                        Header: 'Description',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                    },
                    {
                        id: 'required',
                        Header: 'Required',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.required}/>
                    },
                ]
            }
            totalRowCount={1}
        />

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Response'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <DataTable
            data={[
                {
                    name: '\`code\`',
                    type: 'Int',
                    description: '\`200\` for success.',
                },
                {
                    name: '\`identifiers\`',
                    type: 'List of Objects',
                    description: 'A list of all device IDs that the device owners requested to be deleted.',
                },
            ]}
            columns={
                [
                    {
                        id: 'name',
                        Header: 'Name',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.name}/>
                    },
                    {
                        id: 'type',
                        Header: 'Type',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.type}/>
                    },
                    {
                        id: 'description',
                        Header: 'Description',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                    },
                ]
            }
            totalRowCount={1}
        />

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Errors'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_3}/>

        <DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContentText text={'This API uses HTTP status codes to indicate errors:'}/>
        </DocsPageModule.DocsPageContent>

        <DataTable
            data={[
                {
                    code: '\`200\`',
                    description: 'Success.'
                },
                {
                    code: '\`400\`',
                    description: 'Request is not formatted properly, is missing data, or has an invalid date range specified.'
                },
                {
                    code: '\`403\`',
                    description: 'Authentication failed. Check the validity of the API Key.'
                },
                {
                    code: '\`500\`',
                    description: 'Service is unavailable.'
                },
            ]}
            columns={
                [
                    {
                        id: 'code',
                        Header: 'HTTP Response Code',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.code}/>
                    },
                    {
                        id: 'description',
                        Header: 'Description',
                        sortable: false,
                        Cell: props => <DescriptionCellWrapper text={props.original.description}/>
                    },
                ]
            }
            totalRowCount={1}
        />
        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Example Request'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <CodeSnippet
            snippets={[ {
                title: 'text',
                language: 'bash',
                code: `curl https://api.applovin.com/gdpr_dsp/list_idfa?api_key=<YOUR_GDPR_API_KEY>`
            } ]}
        />

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'With date range:'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_4}/>

        <CodeSnippet
            snippets={[ {
                title: 'text',
                language: 'bash',
                code: `curl https://api.applovin.com/gdpr_dsp/list_idfa?api_key=<YOUR_GDPR_API_KEY>&start_date=2018-07-20&end_date=2018-07-27`
            } ]}
        />

        <DocsPageModule.DocsPageHeader linkTargetTopPosition={linkTargetTopPosition} text={'Example Response'}
                                       type={DocsPageModule.DocsPageHeaderType.HEADING_2}/>

        <CodeSnippet
            snippets={[ {
                title: 'text',
                language: 'json',
                code: `{
    {
    "code": 200,
    "identifiers": [
        {
            "identifier": "03464f18-0657-4823-ba98-3d92a8b8e44a"
        },
        {
            "identifier": "0576a8c3-735a-479a-9fdd-47c20c7b74a0"
        },
        {
            "identifier": "0b20fdb7-8cc7-4dd3-9c34-803b4ebb7fcd"
        },
        {
            "identifier": "0ee90410-5b07-4fca-b9b2-eaab0d9670c9"
        },
        {
            "identifier": "1195ff98-6b12-482f-83ef-095f51e46a70"
        },
        {
            "identifier": "18bd1239-434a-412b-9bf3-c4bb621db66b"
        },
        {
            "identifier": "241d6332-4d86-4b18-b03e-b3072adaa8c5"
        },
        {
            "identifier": "29a24673-b0a8-4ac0-862b-15f3abbfc3c3"
        }
    ],
    "count": 8
   }
}`
            } ]}
        />
    </> )
}

const DescriptionCellWrapper = styled( DocsPageModule.DocsPageContent )`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  white-space: normal;
`;

export default DspDeletedDeviceIdsApiPage;
